import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Sparkles, Plus, Search, CalendarPlus, Power, Trash2, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';
import { format, addDays, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { ListingPicker, type PickedListing, type ListingType } from './internal/ListingPicker';
import { BoostOrdersPanel } from './internal/BoostOrdersPanel';
import { logAdminAction } from '@/lib/auditLog';

type Tier = 'hot' | 'premium' | 'spotlight';
type Placement = 'home' | 'top_of_search' | 'sidebar';
type StatusTab = 'active' | 'scheduled' | 'expired';

const TIER_LABEL: Record<string, string> = {
  hot: 'Hot',
  premium: 'Premium',
  spotlight: 'Spotlight',
  super_hot: 'Super Hot',
  standard: 'Standard',
};

const TIER_BADGE: Record<string, string> = {
  hot: 'bg-orange-100 text-orange-800 border-orange-300',
  premium: 'bg-purple-100 text-purple-800 border-purple-300',
  spotlight: 'bg-amber-100 text-amber-800 border-amber-300',
  super_hot: 'bg-red-100 text-red-800 border-red-300',
  standard: 'bg-slate-100 text-slate-700 border-slate-300',
};

export function FeaturedConsole() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<StatusTab>('active');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [tierFilter, setTierFilter] = useState<string>('all');
  const [placementFilter, setPlacementFilter] = useState<string>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Boost dialog state
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<PickedListing | null>(null);
  const [pickedType, setPickedType] = useState<ListingType>('property');
  const [tier, setTier] = useState<Tier>('hot');
  const [placement, setPlacement] = useState<Placement>('home');
  const [priority, setPriority] = useState(0);
  const [days, setDays] = useState(30);
  const [price, setPrice] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [autoRenew, setAutoRenew] = useState(false);

  // Edit dates dialog
  const [editing, setEditing] = useState<any | null>(null);
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');

  // Deactivate reason dialog
  const [deactivating, setDeactivating] = useState<any | null>(null);
  const [deactivateReason, setDeactivateReason] = useState('');

  // Top-level tab: boosts vs orders
  const [topTab, setTopTab] = useState<'boosts' | 'orders'>('boosts');

  // Last expiry sweep result
  const [expiryRunning, setExpiryRunning] = useState(false);
  const [lastExpiry, setLastExpiry] = useState<{ at: string; affected: number | null; error: string | null } | null>(() => {
    try {
      const raw = localStorage.getItem('featured-last-expiry');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  const { data: rows, isLoading } = useQuery({
    queryKey: ['featured-console'],
    queryFn: async () => {
      const { data } = await supabase
        .from('featured_listings')
        .select('*')
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });
      return data || [];
    },
  });

  // Fetch listing details (titles, images) for visible rows
  const { data: enriched } = useQuery({
    queryKey: ['featured-console-enriched', rows?.length],
    enabled: !!rows && rows.length > 0,
    queryFn: async () => {
      const propertyIds = rows!.filter(r => r.listing_type === 'property').map(r => r.listing_id);
      const plotIds = rows!.filter(r => r.listing_type === 'plot').map(r => r.listing_id);
      const projectIds = rows!.filter(r => r.listing_type === 'project').map(r => r.listing_id);

      const [props, plots, projs, propImgs, plotImgs, projImgs] = await Promise.all([
        propertyIds.length
          ? supabase.from('properties').select('id, title, city, reference_code').in('id', propertyIds)
          : Promise.resolve({ data: [] as any[] }),
        plotIds.length
          ? supabase.from('plots').select('id, title, city, reference_code').in('id', plotIds)
          : Promise.resolve({ data: [] as any[] }),
        projectIds.length
          ? supabase.from('projects').select('id, name, city, reference_code').in('id', projectIds)
          : Promise.resolve({ data: [] as any[] }),
        propertyIds.length
          ? supabase.from('property_images').select('property_id, url, is_primary').in('property_id', propertyIds)
          : Promise.resolve({ data: [] as any[] }),
        plotIds.length
          ? supabase.from('plot_images').select('plot_id, url, is_primary').in('plot_id', plotIds)
          : Promise.resolve({ data: [] as any[] }),
        projectIds.length
          ? supabase.from('project_images').select('project_id, url, is_primary').in('project_id', projectIds)
          : Promise.resolve({ data: [] as any[] }),
      ]);

      const buildImgMap = (items: any[], key: string) => {
        const m = new Map<string, string>();
        items.forEach((i: any) => { if (!m.has(i[key]) || i.is_primary) m.set(i[key], i.url); });
        return m;
      };
      const propMap = new Map((props.data || []).map((p: any) => [p.id, { title: p.title?.en || p.title?.am || 'Untitled', city: p.city, ref: p.reference_code }]));
      const plotMap = new Map((plots.data || []).map((p: any) => [p.id, { title: p.title?.en || p.title?.am || 'Untitled', city: p.city, ref: p.reference_code }]));
      const projMap = new Map((projs.data || []).map((p: any) => [p.id, { title: p.name?.en || p.name?.am || 'Untitled', city: p.city, ref: p.reference_code }]));
      const propImgMap = buildImgMap(propImgs.data || [], 'property_id');
      const plotImgMap = buildImgMap(plotImgs.data || [], 'plot_id');
      const projImgMap = buildImgMap(projImgs.data || [], 'project_id');

      return rows!.map((r: any) => {
        const meta = r.listing_type === 'property' ? propMap.get(r.listing_id)
          : r.listing_type === 'plot' ? plotMap.get(r.listing_id)
          : projMap.get(r.listing_id);
        const img = r.listing_type === 'property' ? propImgMap.get(r.listing_id)
          : r.listing_type === 'plot' ? plotImgMap.get(r.listing_id)
          : projImgMap.get(r.listing_id);
        return { ...r, _title: meta?.title || '—', _city: meta?.city || '—', _ref: meta?.ref || '—', _image: img || null };
      });
    },
  });

  const today = new Date().toISOString().slice(0, 10);

  const filtered = useMemo(() => {
    const base = enriched ?? rows ?? [];
    return base.filter((r: any) => {
      // Tab filter
      if (tab === 'active' && !(r.is_active && r.start_date <= today && r.end_date >= today)) return false;
      if (tab === 'scheduled' && !(r.is_active && r.start_date > today)) return false;
      if (tab === 'expired' && !(r.end_date < today || !r.is_active)) return false;
      // Other filters
      if (typeFilter !== 'all' && r.listing_type !== typeFilter) return false;
      if (tierFilter !== 'all' && r.tier !== tierFilter) return false;
      if (placementFilter !== 'all' && r.placement !== placementFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const t = (r._title || '').toLowerCase();
        const c = (r._city || '').toLowerCase();
        const ref = (r._ref || '').toLowerCase();
        if (!t.includes(q) && !c.includes(q) && !ref.includes(q)) return false;
      }
      return true;
    });
  }, [enriched, rows, tab, typeFilter, tierFilter, placementFilter, search, today]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((r: any) => r.id)));
  };

  const refresh = () => qc.invalidateQueries({ queryKey: ['featured-console'] });

  const handleBoost = async () => {
    if (!user || !picked) return;
    const start = new Date().toISOString().slice(0, 10);
    const end = addDays(new Date(), days).toISOString().slice(0, 10);
    const storedType = picked.type === 'commercial' ? 'property' : picked.type;
    const { data, error } = await supabase.from('featured_listings').insert({
      listing_type: storedType,
      listing_id: picked.id,
      tier,
      placement,
      priority,
      start_date: start,
      end_date: end,
      price_paid: parseFloat(price) || 0,
      payment_ref: paymentRef || null,
      auto_renew: autoRenew,
      created_by: user.id,
      is_active: true,
    } as any).select('id').single();
    if (error) { toast.error(error.message); return; }
    await logAdminAction({
      action: 'boost_listing',
      entity_type: 'featured_listing',
      entity_id: data?.id,
      metadata: { listing_type: storedType, listing_id: picked.id, tier, placement, days, price_paid: parseFloat(price) || 0 },
    });
    toast.success('Listing boosted');
    setOpen(false); setPicked(null); setPrice(''); setPaymentRef(''); setPriority(0); setAutoRenew(false);
    refresh();
  };

  const handleExtend = async (row: any, extraDays = 30) => {
    const newEnd = addDays(parseISO(row.end_date), extraDays).toISOString().slice(0, 10);
    const { error } = await supabase.from('featured_listings').update({ end_date: newEnd, is_active: true }).eq('id', row.id);
    if (error) { toast.error(error.message); return; }
    await logAdminAction({ action: 'extend', entity_type: 'featured_listing', entity_id: row.id, metadata: { extra_days: extraDays, new_end: newEnd } });
    toast.success(`Extended by ${extraDays} days`);
    refresh();
  };

  const handleEditDates = async () => {
    if (!editing) return;
    const { error } = await supabase.from('featured_listings').update({
      start_date: editStart,
      end_date: editEnd,
    }).eq('id', editing.id);
    if (error) { toast.error(error.message); return; }
    await logAdminAction({ action: 'edit_dates', entity_type: 'featured_listing', entity_id: editing.id, metadata: { start_date: editStart, end_date: editEnd } });
    toast.success('Dates updated');
    setEditing(null);
    refresh();
  };

  const handleDeactivate = async () => {
    if (!deactivating) return;
    const { error } = await supabase.from('featured_listings').update({ is_active: false }).eq('id', deactivating.id);
    if (error) { toast.error(error.message); return; }
    await logAdminAction({ action: 'deactivate', entity_type: 'featured_listing', entity_id: deactivating.id, reason: deactivateReason || undefined });
    toast.success('Boost deactivated');
    setDeactivating(null); setDeactivateReason('');
    refresh();
  };

  const handleDelete = async (row: any) => {
    if (!confirm('Permanently delete this boost record?')) return;
    const { error } = await supabase.from('featured_listings').delete().eq('id', row.id);
    if (error) { toast.error(error.message); return; }
    await logAdminAction({ action: 'delete', entity_type: 'featured_listing', entity_id: row.id });
    toast.success('Boost deleted');
    refresh();
  };

  const bulkDeactivate = async () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const { error } = await supabase.from('featured_listings').update({ is_active: false }).in('id', ids);
    if (error) { toast.error(error.message); return; }
    await Promise.all(ids.map(id => logAdminAction({ action: 'bulk_deactivate', entity_type: 'featured_listing', entity_id: id })));
    toast.success(`Deactivated ${ids.length} boosts`);
    setSelectedIds(new Set());
    refresh();
  };

  const bulkExtend = async () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const target = filtered.filter((r: any) => ids.includes(r.id));
    await Promise.all(target.map(async (r: any) => {
      const newEnd = addDays(parseISO(r.end_date), 30).toISOString().slice(0, 10);
      await supabase.from('featured_listings').update({ end_date: newEnd, is_active: true }).eq('id', r.id);
      await logAdminAction({ action: 'bulk_extend', entity_type: 'featured_listing', entity_id: r.id, metadata: { extra_days: 30 } });
    }));
    toast.success(`Extended ${ids.length} boosts by 30 days`);
    setSelectedIds(new Set());
    refresh();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Sparkles className="h-6 w-6 text-primary" /> Featured / Boost Console</h1>
          <p className="text-sm text-muted-foreground">Promote properties, plots, and projects across the platform.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={expiryRunning} onClick={async () => {
            setExpiryRunning(true);
            const { data, error } = await supabase.rpc('expire_featured_listings' as any);
            const at = new Date().toISOString();
            if (error) {
              const next = { at, affected: null, error: error.message };
              setLastExpiry(next);
              try { localStorage.setItem('featured-last-expiry', JSON.stringify(next)); } catch {}
              toast.error(error.message);
            } else {
              const affected = (data as number) ?? 0;
              const next = { at, affected, error: null };
              setLastExpiry(next);
              try { localStorage.setItem('featured-last-expiry', JSON.stringify(next)); } catch {}
              await logAdminAction({ action: 'manual_expire_sweep', entity_type: 'featured_listing', metadata: { affected } });
              toast.success(`Deactivated ${affected} expired boost(s)`);
              refresh();
            }
            setExpiryRunning(false);
          }}><RefreshCw className={`h-4 w-4 mr-2 ${expiryRunning ? 'animate-spin' : ''}`} /> Run expiry now</Button>
          <Button onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-2" /> Boost a listing</Button>
        </div>
      </div>

      {/* Last expiry sweep result banner */}
      {lastExpiry && (
        <div className={`rounded-md border p-3 text-sm flex items-start gap-2 ${
          lastExpiry.error ? 'border-destructive/40 bg-destructive/5 text-destructive' : 'border-primary/30 bg-primary/5'
        }`}>
          {lastExpiry.error ? <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> : <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-primary" />}
          <div className="flex-1">
            <p className="font-medium">
              Last expiry sweep: {format(parseISO(lastExpiry.at), 'PPpp')}
            </p>
            {lastExpiry.error
              ? <p className="text-xs mt-0.5">RPC error: {lastExpiry.error}</p>
              : <p className="text-xs text-muted-foreground mt-0.5">Deactivated {lastExpiry.affected ?? 0} expired boost(s).</p>}
          </div>
        </div>
      )}

      <Tabs value={topTab} onValueChange={(v) => setTopTab(v as 'boosts' | 'orders')}>
        <TabsList>
          <TabsTrigger value="boosts">Active boosts</TabsTrigger>
          <TabsTrigger value="orders">Boost orders</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-4">
          <BoostOrdersPanel onActivated={refresh} />
        </TabsContent>

        <TabsContent value="boosts" className="mt-4">
      <Card>
        <CardHeader className="pb-3">
          <Tabs value={tab} onValueChange={(v) => setTab(v as StatusTab)}>
            <TabsList>
              <TabsTrigger value="active">Active</TabsTrigger>
              <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
              <TabsTrigger value="expired">Expired</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search title, city, ref…" value={search} onChange={e => setSearch(e.target.value)} className="pl-8" />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="property">Property</SelectItem>
                <SelectItem value="plot">Plot</SelectItem>
                <SelectItem value="project">Project</SelectItem>
              </SelectContent>
            </Select>
            <Select value={tierFilter} onValueChange={setTierFilter}>
              <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tiers</SelectItem>
                <SelectItem value="hot">Hot</SelectItem>
                <SelectItem value="premium">Premium</SelectItem>
                <SelectItem value="spotlight">Spotlight</SelectItem>
                <SelectItem value="super_hot">Super Hot</SelectItem>
                <SelectItem value="standard">Standard</SelectItem>
              </SelectContent>
            </Select>
            <Select value={placementFilter} onValueChange={setPlacementFilter}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All placements</SelectItem>
                <SelectItem value="home">Home</SelectItem>
                <SelectItem value="top_of_search">Top of search</SelectItem>
                <SelectItem value="sidebar">Sidebar</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md">
              <span className="text-sm font-medium">{selectedIds.size} selected</span>
              <Button size="sm" variant="outline" onClick={bulkExtend}><CalendarPlus className="h-3.5 w-3.5 mr-1" /> Extend 30d</Button>
              <Button size="sm" variant="outline" onClick={bulkDeactivate}><Power className="h-3.5 w-3.5 mr-1" /> Deactivate</Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>Clear</Button>
            </div>
          )}

          {isLoading ? (
            <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">No boosts match the current filter.</p>
          ) : (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]"><Checkbox checked={selectedIds.size === filtered.length && filtered.length > 0} onCheckedChange={toggleSelectAll} /></TableHead>
                    <TableHead>Listing</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Placement</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r: any) => {
                    const isExpired = r.end_date < today;
                    const isScheduled = r.start_date > today;
                    const status = !r.is_active ? 'Inactive' : isExpired ? 'Expired' : isScheduled ? 'Scheduled' : 'Active';
                    return (
                      <TableRow key={r.id}>
                        <TableCell><Checkbox checked={selectedIds.has(r.id)} onCheckedChange={() => toggleSelect(r.id)} /></TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {r._image ? <img src={r._image} alt="" className="h-9 w-9 rounded object-cover" /> : <div className="h-9 w-9 rounded bg-muted" />}
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate max-w-[220px]">{r._title || '—'}</p>
                              <p className="text-xs text-muted-foreground truncate">{r._city} · {r._ref}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="capitalize text-sm">{r.listing_type}</TableCell>
                        <TableCell><Badge variant="outline" className={TIER_BADGE[r.tier] || ''}>{TIER_LABEL[r.tier] || r.tier}</Badge></TableCell>
                        <TableCell className="capitalize text-sm">{(r.placement || 'home').replace(/_/g, ' ')}</TableCell>
                        <TableCell className="text-sm">{r.priority ?? 0}</TableCell>
                        <TableCell className="text-xs">
                          {format(parseISO(r.start_date), 'MMM d')} – {format(parseISO(r.end_date), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell className="text-sm">ETB {Number(r.price_paid || 0).toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge variant={status === 'Active' ? 'default' : status === 'Scheduled' ? 'secondary' : 'outline'}>{status}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => { setEditing(r); setEditStart(r.start_date); setEditEnd(r.end_date); }}>Edit</Button>
                            <Button size="sm" variant="ghost" onClick={() => handleExtend(r, 30)}>+30d</Button>
                            {r.is_active && <Button size="sm" variant="ghost" onClick={() => setDeactivating(r)}><Power className="h-3.5 w-3.5" /></Button>}
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(r)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>

      {/* Boost dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Boost a listing</DialogTitle>
            <DialogDescription>Promote a property, plot, or project across selected placements.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <ListingPicker value={picked} onChange={setPicked} initialType={pickedType} onTypeChange={setPickedType} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tier</Label>
                <Select value={tier} onValueChange={(v) => setTier(v as Tier)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hot">Hot</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="spotlight">Spotlight</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Placement</Label>
                <Select value={placement} onValueChange={(v) => setPlacement(v as Placement)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="home">Home</SelectItem>
                    <SelectItem value="top_of_search">Top of search</SelectItem>
                    <SelectItem value="sidebar">Sidebar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Duration (days)</Label>
                <Input type="number" min={1} value={days} onChange={e => setDays(Math.max(1, parseInt(e.target.value) || 1))} />
              </div>
              <div>
                <Label>Priority (higher first)</Label>
                <Input type="number" value={priority} onChange={e => setPriority(parseInt(e.target.value) || 0)} />
              </div>
              <div>
                <Label>Price paid (ETB)</Label>
                <Input type="number" min={0} value={price} onChange={e => setPrice(e.target.value)} placeholder="0" />
              </div>
              <div>
                <Label>Payment ref (optional)</Label>
                <Input value={paymentRef} onChange={e => setPaymentRef(e.target.value)} placeholder="TX-…" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="auto-renew" checked={autoRenew} onCheckedChange={setAutoRenew} />
              <Label htmlFor="auto-renew" className="cursor-pointer">Auto-renew at expiry</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleBoost} disabled={!picked}>Boost</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dates dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit dates</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start date</Label><Input type="date" value={editStart} onChange={e => setEditStart(e.target.value)} /></div>
            <div><Label>End date</Label><Input type="date" value={editEnd} onChange={e => setEditEnd(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleEditDates}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate reason dialog */}
      <Dialog open={!!deactivating} onOpenChange={(o) => !o && setDeactivating(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate boost</DialogTitle>
            <DialogDescription>Provide an optional reason. The listing will stop appearing immediately.</DialogDescription>
          </DialogHeader>
          <Textarea placeholder="Reason (optional)" value={deactivateReason} onChange={e => setDeactivateReason(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivating(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeactivate}>Deactivate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
